import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import SubscriptionManager from './components/subscription/SubscriptionManager';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import api from './services/api';
import booksService from './services/books.service';
import { examApi } from './services/exam.service';
import MobileAppBridge from './components/mobile/MobileAppBridge';
import RouteChangeReset from './components/routing/RouteChangeReset';
import {
  EDUCATION_FOCUS_REDIRECT,
  isRidesModuleEnabled,
  isSportsModuleEnabled,
} from './config/modules';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import PrivateRoute from './components/auth/PrivateRoute';
import Dashboard from './pages/Dashboard';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import TeacherSubscriptionPlans from './pages/teacher/TeacherSubscriptionPlans';
import TeacherPayments from './pages/teacher/TeacherPayments';
import TeacherLeads from './pages/teacher/TeacherLeads';
import TeacherCertificationExam from './pages/teacher/TeacherCertificationExam';
import StudentDashboard from './pages/student/StudentDashboard';
import StudentSubscriptionPlans from './pages/student/StudentSubscriptionPlans';
import FindTeacher from './pages/student/FindTeacher';
import SelectTeacher from './pages/student/SelectTeacher';
import WalletTopupPayment from './pages/student/WalletTopupPayment';
import StudentProgress from './pages/student/StudentProgress';
import StudentCertificates from './pages/student/StudentCertificates';
import StudentProfile from './pages/student/StudentProfile';
import MeetingVerify from './pages/student/MeetingVerify';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import ExamsPage from './pages/ExamsPage';
import PublicExamDashboard from './pages/PublicExamDashboard';
import ExamDetailsPage from './pages/ExamDetailsPage';
import TeacherPage from './pages/TeacherPage';
import JobsPage from './pages/JobsPage';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import Feedback from './pages/Feedback';
import AccountDeletionPage from './pages/AccountDeletionPage';
import AccountPrivacyPage from './pages/AccountPrivacyPage';
import CertificateVerificationPage from './pages/CertificateVerificationPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionPaymentPage from './pages/SubscriptionPaymentPage';
import SafetySOSPage from './pages/SafetySOSPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ExamManager from './components/teacher/ExamManager';
import StudentExamList from './components/students/StudentExamList';
import AdminScheduleDashboard from './pages/admin/AdminScheduleDashboard';
import AdminOperationsDashboard from './pages/admin/AdminOperationsDashboard';
import PublicLayout from './components/layout/PublicLayout';
import ScholarshipPage from './pages/scholarship/ScholarshipPage';
import ScholarshipExamPage from './pages/scholarship/ScholarshipExamPage';
import ScholarshipResultsPage from './pages/scholarship/ScholarshipResultsPage';
import AdminScholarshipPage from './pages/admin/AdminScholarshipPage';
import {
  CompetitiveExamPage,
  CompetitiveExamsPage,
  EducationBoardPage,
  EducationClassPage,
  EducationClassesPage,
  EducationLandingPage,
  EducationSubjectTutorsPage,
  EducationTutorsPage,
  NearbyAreaTutorsPage,
  StudentRegistrationSeoPage,
  TutorRegistrationSeoPage,
} from './pages/education';
import {
  CoachingInstitutePage,
  ComplaintsReviewsPage,
  DemoClassesPage,
  EducationAnalyticsPage,
  EducationOperationsHubPage,
  EducationRemindersPage,
  HomeworkTasksPage,
  ParentDashboardPage,
  PaymentReconciliationPage,
  RefundWorkflowPage,
  ReviewModerationPage,
  StaffRbacPage,
  StudentRequirementsPage,
  SubscriptionEnhancementsPage,
  TutorVerificationPage,
} from './pages/education/EducationOperationsPages';
import {
  AdminBooksPage,
  BookActionPage,
  BookListingDetailPage,
  BookShopDetailPage,
  BookShopsPage,
  BooksLandingPage,
  BooksSearchPage,
  MyBookListingsPage,
  MyBookRequestsPage,
  RequestFromShopsPage,
  ShopBuybackOffersPage,
  ShopDashboardPage,
  ShopProductAddPage,
  ShopRegisterPage,
  ShopRequestsPage,
  ShopSubscriptionPage,
} from './pages/books/BooksStationeryPages';
import {
  AdminSportsPage,
  ChallengeMatchPage,
  ClaimProfilePage,
  CreateTeamPage,
  CreateTournamentPage,
  GroundsPage,
  RegisterPlayerPage,
  SportsDashboardPage,
  SportsHomePage,
  SportsProfilePage,
  SportsRegistrationPage,
  SponsorsPage,
  TeamDetailPage,
  TeamsPage,
  TournamentDetailPage,
  TournamentsPage,
} from './pages/sports/SportsConnectPages';
import {
  AdminRidesPage,
  BookRidePage,
  RegisterDriverPage,
  RegisterStandPage,
  RidesDashboardPage,
  RidesHomePage,
  RidesRegistrationPage,
  RidesStandsPage,
} from './pages/rides/RidesPages';

const SUBSCRIPTION_WARNING = 'Please select at least one plan to continue.';

const TutorDeepLinkRedirect = () => {
  const { teacherId = '' } = useParams();
  return <Navigate to={`/student/teachers/${encodeURIComponent(teacherId)}/select`} replace />;
};

const hasActiveSubscription = async (role: 'TEACHER' | 'STUDENT'): Promise<boolean> => {
  try {
    if (role === 'STUDENT') {
      return true;
    }

    if (role === 'TEACHER') {
      const response = await api.get('/teachers/subscription/status');
      return Boolean(response.data?.hasActiveSubscription);
    }

    return false;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return false;
    }
    return false;
  }
};

const getTeacherApprovalStatus = async (): Promise<{
  isApproved: boolean;
  certificationPassed: boolean;
  profileComplete: boolean;
  ready: boolean;
}> => {
  try {
    const profileResponse = await api.get('/teachers/profile');
    const profileData = profileResponse.data || {};
    const profileStatus = String(profileData?.status || '').toUpperCase();
    const isApproved = profileStatus === 'APPROVED';
    const profileComplete = Boolean(
      profileData?.profileComplete ||
      Number(profileData?.profileCompletionPercentage || 0) >= 100,
    );

    // Prefer profile-backed certification/readiness fields because backend already computes them.
    let certificationPassed = Boolean(
      profileData?.certificationPassed || profileData?.isReadyForStudentVisibility || profileData?.leadsEnabled,
    );

    if (!certificationPassed) {
      try {
        const certificationStatus = await examApi.getTeacherCertificationStatus();
        certificationPassed = Boolean(certificationStatus?.isPassed);
      } catch {
        certificationPassed = false;
      }
    }

    const readyFromProfile = Boolean(
      profileData?.isReadyForStudentVisibility || profileData?.leadsEnabled,
    );
    return {
      isApproved,
      certificationPassed,
      profileComplete,
      ready: readyFromProfile || (isApproved && certificationPassed && profileComplete),
    };
  } catch (error) {
    return { isApproved: false, certificationPassed: false, profileComplete: false, ready: false };
  }
};

const hasBookShopProfile = async (): Promise<boolean> => {
  try {
    const shops = await booksService.myBookShops();
    return Array.isArray(shops) && shops.length > 0;
  } catch {
    return false;
  }
};

const RoleBasedRedirect = () => {
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;

  const [targetPath, setTargetPath] = useState<string | null>(null);
  const [navigationState, setNavigationState] = useState<Record<string, string> | undefined>(undefined);

  useEffect(() => {
    const resolveRedirect = async () => {
      if (!currentUser?.role) {
        setTargetPath('/');
        setNavigationState(undefined);
        return;
      }

      if (currentUser.role === 'ADMIN') {
        setTargetPath('/admin/schedule-dashboard');
        setNavigationState(undefined);
        return;
      }

      if (currentUser.role === 'SPORTS') {
        setTargetPath(isSportsModuleEnabled ? '/sports/dashboard' : EDUCATION_FOCUS_REDIRECT);
        setNavigationState(undefined);
        return;
      }

      if (currentUser.role === 'RIDES') {
        setTargetPath(isRidesModuleEnabled ? '/rides/dashboard' : EDUCATION_FOCUS_REDIRECT);
        setNavigationState(undefined);
        return;
      }

      if (currentUser.role === 'TEACHER') {
        const teacherStatus = await getTeacherApprovalStatus();
        if (!teacherStatus.ready) {
          setTargetPath('/teacher/dashboard?tab=profile&showCertification=1');
          setNavigationState(undefined);
          return;
        }

        const isSubscribed = await hasActiveSubscription('TEACHER');
        if (isSubscribed) {
          setTargetPath('/teacher/dashboard');
          setNavigationState(undefined);
          return;
        }
        setTargetPath('/teacher/dashboard');
        setNavigationState({ subscriptionWarning: SUBSCRIPTION_WARNING });
        return;
      }

      if (currentUser.role === 'STUDENT') {
        const hasSellerProfile = await hasBookShopProfile();
        if (hasSellerProfile) {
          setTargetPath('/shop/dashboard');
          setNavigationState(undefined);
          return;
        }

        const isSubscribed = await hasActiveSubscription('STUDENT');
        if (isSubscribed) {
          setTargetPath('/student/dashboard');
          setNavigationState(undefined);
          return;
        }
        setTargetPath('/subscriptions');
        setNavigationState({ subscriptionWarning: SUBSCRIPTION_WARNING });
        return;
      }

      if (currentUser.role === 'SELLER') {
        setTargetPath('/shop/dashboard');
        setNavigationState(undefined);
        return;
      }

      setTargetPath('/');
      setNavigationState(undefined);
    };

    resolveRedirect();
  }, [currentUser?.role]);

  if (!targetPath) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return <Navigate to={targetPath} state={navigationState} replace />;
};

const RequireActiveSubscription: React.FC<{
  role: 'TEACHER' | 'STUDENT';
  children: React.ReactElement;
}> = ({ role, children }) => {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      const isSubscribed = await hasActiveSubscription(role);
      setHasAccess(isSubscribed);
    };

    checkAccess();
  }, [role]);

  if (hasAccess === null) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    const redirectPath = role === 'TEACHER' ? '/teacher/subscription-plans' : '/subscriptions';
    return <Navigate to={redirectPath} state={{ subscriptionWarning: SUBSCRIPTION_WARNING }} replace />;
  }

  return children;
};

const RequireTeacherApproval: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const [approved, setApproved] = useState<boolean | null>(null);

  useEffect(() => {
    const checkApproval = async () => {
      const teacherStatus = await getTeacherApprovalStatus();
      setApproved(teacherStatus.ready);
    };
    checkApproval();
  }, []);

  if (approved === null) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!approved) {
    return <Navigate to="/teacher/dashboard?tab=profile&showCertification=1" replace />;
  }

  return children;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <MobileAppBridge />
        <RouteChangeReset />
        <Routes>
          {/* Public routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/education" element={<EducationLandingPage />} />
            <Route path="/education/tutors" element={<EducationTutorsPage />} />
            <Route path="/education/tutor-registration" element={<TutorRegistrationSeoPage />} />
            <Route path="/education/student-registration" element={<StudentRegistrationSeoPage />} />
            <Route path="/education/classes" element={<EducationClassesPage />} />
            <Route path="/education/classes/:classSlug" element={<EducationClassPage />} />
            <Route path="/education/class-9" element={<Navigate to="/education/classes/class-9" replace />} />
            <Route path="/education/class-10" element={<Navigate to="/education/classes/class-10" replace />} />
            <Route path="/education/boards/:boardSlug" element={<EducationBoardPage />} />
            <Route path="/education/competitive-exams" element={<CompetitiveExamsPage />} />
            <Route path="/education/competitive-exams/:examSlug" element={<CompetitiveExamPage />} />
            <Route path="/education/tutors/nearby/:areaSlug" element={<NearbyAreaTutorsPage />} />
            <Route path="/education/tutors/:subjectSlug" element={<EducationSubjectTutorsPage />} />
            <Route path="/books" element={<BooksLandingPage />} />
            <Route path="/books/search" element={<BooksSearchPage />} />
            <Route path="/books/listings/:id" element={<BookListingDetailPage />} />
            <Route path="/books/:id" element={<BookListingDetailPage />} />
            <Route path="/books/sell" element={<PrivateRoute><BookActionPage mode="SELL" /></PrivateRoute>} />
            <Route path="/books/exchange" element={<PrivateRoute><BookActionPage mode="EXCHANGE" /></PrivateRoute>} />
            <Route path="/books/donate" element={<PrivateRoute><BookActionPage mode="DONATE" /></PrivateRoute>} />
            <Route path="/books/request-from-shops" element={<PrivateRoute><RequestFromShopsPage /></PrivateRoute>} />
            <Route path="/books/sell-to-shops" element={<PrivateRoute><BookActionPage mode="SELL_TO_SHOPS" /></PrivateRoute>} />
            {isSportsModuleEnabled ? (
              <>
                <Route path="/sports" element={<SportsHomePage />} />
                <Route path="/sports/register" element={<SportsRegistrationPage />} />
                <Route path="/sports/register-player" element={<RegisterPlayerPage />} />
                <Route path="/sports/create-team" element={<CreateTeamPage />} />
                <Route path="/sports/dashboard" element={
                  <PrivateRoute requiredRole="SPORTS">
                    <SportsDashboardPage />
                  </PrivateRoute>
                } />
                <Route path="/sports/teams" element={<TeamsPage />} />
                <Route path="/sports/teams/:teamId" element={<TeamDetailPage />} />
                <Route path="/sports/challenge-match" element={<ChallengeMatchPage />} />
                <Route path="/sports/tournaments" element={<TournamentsPage />} />
                <Route path="/sports/tournaments/create" element={<CreateTournamentPage />} />
                <Route path="/sports/tournaments/:tournamentId" element={<TournamentDetailPage />} />
                <Route path="/sports/grounds" element={<GroundsPage />} />
                <Route path="/sports/sponsors" element={<SponsorsPage />} />
                <Route path="/sports/profile" element={
                  <PrivateRoute requiredRole="SPORTS">
                    <SportsProfilePage />
                  </PrivateRoute>
                } />
                <Route path="/sports/claim-profile" element={<ClaimProfilePage />} />
              </>
            ) : (
              <Route path="/sports/*" element={<Navigate to={EDUCATION_FOCUS_REDIRECT} replace />} />
            )}
            {isRidesModuleEnabled ? (
              <>
                <Route path="/rides" element={<RidesHomePage />} />
                <Route path="/rides/register" element={<RidesRegistrationPage />} />
                <Route path="/rides/book" element={<BookRidePage />} />
                <Route path="/rides/driver/register" element={<RegisterDriverPage />} />
                <Route path="/rides/stand/register" element={<RegisterStandPage />} />
                <Route path="/rides/stands" element={<RidesStandsPage />} />
                <Route path="/rides/dashboard" element={
                  <PrivateRoute requiredRole="RIDES">
                    <RidesDashboardPage />
                  </PrivateRoute>
                } />
              </>
            ) : (
              <Route path="/rides/*" element={<Navigate to={EDUCATION_FOCUS_REDIRECT} replace />} />
            )}
            <Route path="/book-shops" element={<BookShopsPage />} />
            <Route path="/book-shops/:id" element={<BookShopDetailPage />} />
            <Route path="/book-shop/register" element={<ShopRegisterPage />} />
            <Route path="/seller/register" element={<Navigate to="/book-shop/register" replace />} />
            <Route path="/shop/register" element={<Navigate to="/book-shop/register" replace />} />
            <Route path="/exams" element={<ExamsPage />} />
            <Route path="/exams/:examId" element={<ExamDetailsPage />} />
            <Route path="/scholarship" element={<ScholarshipPage />} />
            <Route path="/scholarship/exam" element={<ScholarshipExamPage />} />
            <Route path="/scholarship/results" element={<ScholarshipResultsPage />} />
            <Route path="/exam-dashboard" element={<PublicExamDashboard />} />
            <Route path="/exam/:examId" element={<ExamDetailsPage />} />
            <Route path="/tutors/:teacherId" element={<TutorDeepLinkRedirect />} />
            <Route path="/teacher" element={<TeacherPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/account-deletion" element={<AccountDeletionPage />} />
            <Route path="/certificate/verify/:certificateId" element={<CertificateVerificationPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/terms-and-conditions" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPage />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/meeting-verify/:encodedPayload" element={<MeetingVerify />} />
          </Route>
          
          {/* Protected routes */}
          <Route element={<PrivateRoute />}>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<RoleBasedRedirect />} />
              <Route path="/dashboard/legacy" element={<Dashboard />} />
              <Route path="/account/privacy" element={<AccountPrivacyPage />} />
              <Route path="/my-book-listings" element={<MyBookListingsPage />} />
              <Route path="/my-book-requests" element={<MyBookRequestsPage />} />
              <Route path="/shop/dashboard" element={<ShopDashboardPage />} />
              <Route path="/seller/dashboard" element={<ShopDashboardPage />} />
              <Route path="/shop/profile" element={<ShopDashboardPage />} />
              <Route path="/shop/products" element={<ShopDashboardPage />} />
              <Route path="/shop/products/add" element={<ShopProductAddPage />} />
              <Route path="/shop/book-requests" element={<ShopRequestsPage />} />
              <Route path="/shop/buyback-offers" element={<ShopBuybackOffersPage />} />
              <Route path="/shop/enquiries" element={<ShopRequestsPage />} />
              <Route path="/shop/subscription" element={<ShopSubscriptionPage />} />
              <Route path="/teacher/dashboard" element={
                <PrivateRoute requiredRole="TEACHER">
                  <TeacherDashboard />
                </PrivateRoute>
              } />
              <Route path="/teacher/subscription-plans" element={
                <PrivateRoute requiredRole="TEACHER">
                  <RequireTeacherApproval>
                    <TeacherSubscriptionPlans />
                  </RequireTeacherApproval>
                </PrivateRoute>
              } />
              <Route path="/teacher/certification-exam" element={
                <PrivateRoute requiredRole="TEACHER">
                  <TeacherCertificationExam />
                </PrivateRoute>
              } />
              <Route path="/teacher/leads" element={
                <PrivateRoute requiredRole="TEACHER">
                  <RequireTeacherApproval>
                    <TeacherLeads />
                  </RequireTeacherApproval>
                </PrivateRoute>
              } />
              <Route path="/teacher/classes" element={
                <PrivateRoute requiredRole="TEACHER">
                  <RequireTeacherApproval>
                    <Navigate to="/teacher/dashboard?tab=sessions" replace />
                  </RequireTeacherApproval>
                </PrivateRoute>
              } />
              <Route path="/teacher/payments" element={
                <PrivateRoute requiredRole="TEACHER">
                  <RequireTeacherApproval>
                    <TeacherPayments />
                  </RequireTeacherApproval>
                </PrivateRoute>
              } />
              <Route path="/teacher/earnings" element={
                <PrivateRoute requiredRole="TEACHER">
                  <RequireTeacherApproval>
                    <Navigate to="/teacher/payments" replace />
                  </RequireTeacherApproval>
                </PrivateRoute>
              } />
              <Route path="/subscription" element={
                <PrivateRoute requiredRole="TEACHER">
                  <RequireTeacherApproval>
                    <SubscriptionPage />
                  </RequireTeacherApproval>
                </PrivateRoute>
              } />
              <Route path="/student/dashboard" element={
                <PrivateRoute requiredRole="STUDENT">
                  <RequireActiveSubscription role="STUDENT">
                    <StudentDashboard />
                  </RequireActiveSubscription>
                </PrivateRoute>
              } />
              <Route path="/student/subscription-plans" element={
                <PrivateRoute requiredRole="STUDENT">
                  <StudentSubscriptionPlans />
                </PrivateRoute>
              } />
              <Route path="/student/find-teachers" element={<FindTeacher />} />
              <Route path="/student/teachers/:teacherId/select" element={
                <PrivateRoute requiredRole="STUDENT">
                  <SelectTeacher />
                </PrivateRoute>
              } />
              <Route path="/student/wallet/topup" element={
                <PrivateRoute>
                  <WalletTopupPayment />
                </PrivateRoute>
              } />
              <Route path="/teacher/exams" element={
                <PrivateRoute requiredRole="TEACHER">
                  <RequireTeacherApproval>
                    <ExamManager onClose={() => window.history.back()} />
                  </RequireTeacherApproval>
                </PrivateRoute>
              } />
              <Route path="/student/exams" element={
                <PrivateRoute requiredRole="STUDENT">
                  <StudentExamList onClose={() => window.history.back()} />
                </PrivateRoute>
              } />
              <Route path="/student/progress" element={
                <PrivateRoute requiredRole="STUDENT">
                  <StudentProgress />
                </PrivateRoute>
              } />
              <Route path="/student/profile" element={
                <PrivateRoute requiredRole="STUDENT">
                  <StudentProfile />
                </PrivateRoute>
              } />
              <Route path="/parent/dashboard" element={
                <PrivateRoute requiredRole="STUDENT">
                  <ParentDashboardPage />
                </PrivateRoute>
              } />
              <Route path="/student/requirements" element={
                <PrivateRoute requiredRole="STUDENT">
                  <StudentRequirementsPage />
                </PrivateRoute>
              } />
              <Route path="/student/certificates" element={
                <PrivateRoute requiredRole="STUDENT">
                  <StudentCertificates />
                </PrivateRoute>
              } />
              <Route path="/education/operations" element={<EducationOperationsHubPage />} />
              <Route path="/education/institute" element={
                <PrivateRoute requiredRole="TEACHER">
                  <CoachingInstitutePage />
                </PrivateRoute>
              } />
              <Route path="/education/demos" element={<DemoClassesPage />} />
              <Route path="/education/homework" element={<HomeworkTasksPage />} />
              <Route path="/education/complaints" element={<ComplaintsReviewsPage />} />
              <Route path="/education/subscription-usage" element={<SubscriptionEnhancementsPage />} />
              <Route path="/admin/schedule-dashboard" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminScheduleDashboard />
                </PrivateRoute>
              } />
              <Route path="/admin/reschedule-approvals" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminScheduleDashboard />
                </PrivateRoute>
              } />
              <Route path="/admin/operations" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminOperationsDashboard />
                </PrivateRoute>
              } />
              <Route path="/admin/scholarship" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminScholarshipPage />
                </PrivateRoute>
              } />
              <Route path="/admin/books" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/books/listings" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/books/requests" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/books/donations" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/book-shops" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/book-shops/pending" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/book-shops/approved" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/book-shops/rejected" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/shop-products" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/books/reports" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/books/settings" element={
                <PrivateRoute requiredRole="ADMIN">
                  <AdminBooksPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education" element={
                <PrivateRoute requiredRole="ADMIN">
                  <EducationOperationsHubPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education/verification" element={
                <PrivateRoute requiredRole="ADMIN">
                  <TutorVerificationPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education/refunds" element={
                <PrivateRoute requiredRole="ADMIN">
                  <RefundWorkflowPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education/reviews" element={
                <PrivateRoute requiredRole="ADMIN">
                  <ReviewModerationPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education/staff" element={
                <PrivateRoute requiredRole="ADMIN">
                  <StaffRbacPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education/analytics" element={
                <PrivateRoute requiredRole="ADMIN">
                  <EducationAnalyticsPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education/reminders" element={
                <PrivateRoute requiredRole="ADMIN">
                  <EducationRemindersPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education/payments" element={
                <PrivateRoute requiredRole="ADMIN">
                  <PaymentReconciliationPage />
                </PrivateRoute>
              } />
              <Route path="/admin/education/subscriptions" element={
                <PrivateRoute requiredRole="ADMIN">
                  <SubscriptionEnhancementsPage />
                </PrivateRoute>
              } />
              {isSportsModuleEnabled ? (
                <>
                  <Route path="/admin/sports" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminSportsPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/sports/categories" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminSportsPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/sports/players" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminSportsPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/sports/teams" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminSportsPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/sports/tournaments" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminSportsPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/sports/matches" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminSportsPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/sports/grounds" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminSportsPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/sports/sponsors" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminSportsPage />
                    </PrivateRoute>
                  } />
                </>
              ) : (
                <Route path="/admin/sports/*" element={<Navigate to="/admin/operations" replace />} />
              )}
              {isRidesModuleEnabled ? (
                <>
                  <Route path="/admin/rides" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminRidesPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/rides/drivers" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminRidesPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/rides/vehicles" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminRidesPage />
                    </PrivateRoute>
                  } />
                  <Route path="/admin/rides/stands" element={
                    <PrivateRoute requiredRole="ADMIN">
                      <AdminRidesPage />
                    </PrivateRoute>
                  } />
                </>
              ) : (
                <Route path="/admin/rides/*" element={<Navigate to="/admin/operations" replace />} />
              )}
              <Route path="/subscriptions" element={<SubscriptionManager />} />
              <Route path="/subscription/payment" element={<SubscriptionPaymentPage />} />
              <Route path="/sos" element={
                <PrivateRoute>
                  <SafetySOSPage />
                </PrivateRoute>
              } />
            </Route>
          </Route>
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;
